import { motion, AnimatePresence } from 'motion/react';

interface LoadingScreen3DProps {
  worldId?: string;
  levelNumber?: number;
  isTransition?: boolean;
}

const WORLD_COLORS: Record<string, { bg: string; accent: string; name: string }> = {
  greenwood: { bg: '#0a1f12', accent: '#4ecdc4', name: 'Greenwood' },
  stonehollow: { bg: '#12141a', accent: '#90A4AE', name: 'Stone Hollow' },
  frostline: { bg: '#081820', accent: '#4DD0E1', name: 'Frostline' },
  emberdeep: { bg: '#1a0a04', accent: '#FF7043', name: 'Ember Deep' },
  arcana: { bg: '#0a0820', accent: '#AB47BC', name: 'Arcana' },
};

export default function LoadingScreen3D({ worldId = 'greenwood', levelNumber, isTransition = false }: LoadingScreen3DProps) {
  const world = WORLD_COLORS[worldId] || WORLD_COLORS.greenwood;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        background: world.bg,
        zIndex: 100,
      }}
    >
      {/* Level number (for transitions between levels) */}
      {isTransition && levelNumber != null && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          style={{
            color: `${world.accent}88`,
            fontSize: 48,
            fontWeight: 900,
            fontFamily: '"Press Start 2P", monospace',
            letterSpacing: '4px',
          }}
        >
          {levelNumber + 1}
        </motion.div>
      )}

      {/* Animated loading orbs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -12, 0],
              opacity: [0.4, 1, 0.4],
            }}
            transition={{
              duration: 0.8,
              delay: i * 0.15,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: world.accent,
              boxShadow: `0 0 12px ${world.accent}66`,
            }}
          />
        ))}
      </div>

      {/* World name */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={{
          color: world.accent,
          fontSize: 14,
          fontWeight: 800,
          fontFamily: '"Press Start 2P", monospace',
          textShadow: `0 0 16px ${world.accent}44`,
          letterSpacing: '2px',
          textTransform: 'uppercase',
        }}
      >
        {world.name}
      </motion.div>

      {/* Subtitle */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 0.4 }}
        style={{
          color: '#fff',
          fontSize: 12,
          fontFamily: 'system-ui, sans-serif',
          letterSpacing: '1px',
        }}
      >
        {isTransition ? 'Get Ready...' : 'Loading 3D World...'}
      </motion.div>
    </motion.div>
  );
}
